import { takeLatest, put, call } from 'redux-saga/effects'
import NProgress from 'nprogress'

import * as CONST from './const'
import { apiClient } from 'Module/API/Client'
import { authorization } from 'Module/Auth/Auth'
import { push } from 'react-router-redux'
import { PATHS } from 'Config/Routing/paths'
import { ROLE_CLIENT, ROLE_CLIENT_ADMIN } from '../../shared/constants'
import { loadScript } from '../../Utils/Misc'

/**
 * Login user
 *
 * @param {Object} action
 */
function* loginUser(action) {
  NProgress.start()

  const response = yield call(apiClient.userAuth, {
    email: action.payload.username,
    password: action.payload.password,
  })

  try {
    if (response.ok) {
      const body = yield response.json()
      if (
        body.user &&
        [ROLE_CLIENT, ROLE_CLIENT_ADMIN].includes(body.user.role)
      ) {
        loadScript('//js-na1.hs-scripts.com/7519581.js')
      }
      authorization.login({
        user: body.user,
        apiKey: body.apiKey,
        countryName: body.countryName
          ? body.countryName
          : body.city
          ? body.city.country.name
          : null,
        countryCode: body.countryCode
          ? body.countryCode
          : body.city
          ? body.city.country.code
          : null,
      })

      yield put({
        type: CONST.AUTH_LOGIN_SUCCESS,
        payload: {
          user: body.user,
          apiKey: body.apiKey,
        },
      })
      if (body.user.role !== ROLE_CLIENT_ADMIN) {
        window.location.reload()
      }
    } else if (response.status === 400 || response.status === 403) {
      const body = yield response.json()

      yield put({
        type: CONST.AUTH_LOGIN_ERROR,
        payload: {
          errorMessage: body.message,
        },
      })
      authorization.logout()
    }
  } catch (ex) {
    console.error(ex)
  }

  NProgress.done()
}

/**
 * Login user
 *
 * @param {Object} action
 */
function* loginUserOAuth2(action) {
  NProgress.start()

  const response = yield call(apiClient.userOAuth2Auth, {
    email: action.payload.username,
  })

  try {
    if (response.ok) {
      const body = yield response.json()
      if (
        body.user &&
        [ROLE_CLIENT, ROLE_CLIENT_ADMIN].includes(body.user.role)
      ) {
        loadScript('//js-na1.hs-scripts.com/7519581.js')
      }
      authorization.login({
        user: body.user,
        apiKey: body.apiKey,
        countryName: body.countryName
          ? body.countryName
          : body.city
          ? body.city.country.name
          : null,
        countryCode: body.countryCode
          ? body.countryCode
          : body.city
          ? body.city.country.code
          : null,
      })

      yield put({
        type: CONST.AUTH_LOGIN_SUCCESS,
        payload: {
          user: body.user,
          apiKey: body.apiKey,
        },
      })
    } else if (response.status === 400 || response.status === 403) {
      const body = yield response.json()

      yield put({
        type: CONST.AUTH_LOGIN_ERROR,
        payload: {
          errorMessage: body.message,
        },
      })
      authorization.logout()
    }
  } catch (ex) {
    console.error(ex)
  }

  NProgress.done()
}

/**
 * Request reset password
 *
 * @param {Object} action
 */
function* logoutUser(action) {
  NProgress.start()
  yield call(apiClient.logout)
  NProgress.done()
}

/**
 * Request reset password
 *
 * @param {Object} action
 */
function* userRequestResetPassword(action) {
  NProgress.start()
  const response = yield call(apiClient.userRequestResetPassword, {
    email: action.payload.email,
  })

  if (response.ok) {
    yield put({
      type: CONST.AUTH_REQUEST_RESET_PASSWORD_SUCCESS,
      payload: {
        email: action.payload.email,
      },
    })
  } else {
    yield put({
      type: CONST.AUTH_REQUEST_RESET_PASSWORD_ERROR,
      payload: {
        errorMessage: 'Please enter valid email address',
      },
    })
  }

  NProgress.done()
}

/**
 * Check code and hash valid for password resetting
 * @param action
 */
function* userCheckCodeAndHash(action) {
  NProgress.start()
  const response = yield call(
    apiClient.userCheckCodeAndHash,
    action.payload.hash,
    action.payload.code,
  )

  if (response.ok) {
    yield put({
      type: CONST.AUTH_CHECK_CODE_AND_HASH_SUCCESS,
    })
  } else {
    yield put({
      type: CONST.AUTH_CHECK_CODE_AND_HASH_ERROR,
      payload: {
        errorMessage: 'Sorry, the link is invalid.',
      },
    })
  }

  NProgress.done()
}

/**
 * Reset password
 *
 * @param {Object} action
 */
function* userResetPassword(action) {
  const response = yield call(apiClient.userResetPassword, {
    code: action.payload.code,
    hash: action.payload.hash,
    password: action.payload.password,
  })
  const body = yield response.json()
  // If the user is a distributor then just redirect to a
  // "Your password was reset successfully page".
  if (body.user.role == 'DISTRIBUTOR') {
    yield put(push(PATHS.main.resetPassSuccess))
    return
  }

  if (response.ok) {
    if (authorization.isLogged()) {
      authorization.login({
        user: body.user,
        apiKey: body.apiKey,
      })
    }
    yield put({
      type: CONST.AUTH_RESET_PASSWORD_SUCCESS,
      payload: {
        user: body.user,
        apiKey: body.apiKey,
      },
    })
  } else {
    yield put({
      type: CONST.AUTH_RESET_PASSWORD_ERROR,
      payload: {
        errorMessage: body.message,
      },
    })
  }
}

/**
 * Login user watcher
 */
export function* watchLoginUser() {
  yield takeLatest(CONST.AUTH_LOGIN, loginUser)
}

/**
 * Login user watcher
 */
export function* watchLoginUserOAuth2() {
  yield takeLatest(CONST.AUTH_LOGIN_OAUTH2, loginUserOAuth2)
}

/**
 * Logout user watcher
 */
export function* watchLogoutUser() {
  yield takeLatest(CONST.AUTH_LOGOUT, logoutUser)
}

/**
 * Request for resetting the password
 */
export function* watchUserRequestResetPassword() {
  yield takeLatest(CONST.AUTH_REQUEST_RESET_PASSWORD, userRequestResetPassword)
}

/**
 * Check code and hash for password resetting
 */
export function* watchCheckCodeAndHash() {
  yield takeLatest(CONST.AUTH_CHECK_CODE_AND_HASH, userCheckCodeAndHash)
}

/**
 * Resetting password
 */
export function* watchUserResetPassword() {
  yield takeLatest(CONST.AUTH_RESET_PASSWORD, userResetPassword)
}
